'use client';

import { memo } from 'react';
import { VStack } from '@cbhq/cds-web/layout';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { TwoColumnLayout } from '@/components/common/TwoColumnLayout';
import { DEFAULT_CARD_SPACER } from '@/constants/layout';
import { PoolDetailsHeader } from './PoolDetailsHeader';
import { PoolDetailsChart } from './PoolDetailsChart';
import { PoolDetailsSidebar } from './PoolDetailsSidebar/PoolDetailsSidebar';
import { PoolDetailsTransactions } from './PoolDetailsTransactions';
import { usePoolDetailsData, PoolDetailsData } from './hooks/usePoolDetailsData';
import { useExplorePoolActions } from './hooks/useExplorePoolActions';

type Props = {
  poolId: string;
  onBack?: () => void;
};

type MobileFooterProps = {
  pool: PoolDetailsData;
};

/**
 * Mobile footer with action buttons
 */
const MobileFooter = memo(function MobileFooter({ pool }: MobileFooterProps) {
  const { handleSwapClick, handleCreatePositionClick } = useExplorePoolActions(pool);

  return (
    <VStack gap={1} width="100%">
      <Button variant="primary" block onClick={handleCreatePositionClick}>
        Create position
      </Button>
      <Button variant="secondary" block onClick={handleSwapClick}>
        Swap
      </Button>
    </VStack>
  );
});

/**
 * PoolDetailsLayout - Main layout for the Pool Details page
 *
 * Displays detailed information about a specific pool including:
 * - Header with token pair, fee, and action buttons
 * - Price/Volume/Liquidity chart
 * - Sidebar with Earn, Stats, and USDC Rewards
 * - Transaction history table
 */
const PoolDetailsLayout = memo(function PoolDetailsLayout({ poolId, onBack }: Props) {
  const { pool, priceHistory, transactions } = usePoolDetailsData(poolId);
  const { isPhone, isPhoneLandscape, isTablet } = useBreakpoints();
  const isMobile = isPhone || isPhoneLandscape || isTablet;

  return (
    <VStack width="100vw" background="bgAlternate" minHeight="100vh">
      <TwoColumnLayout
        header={<PoolDetailsHeader pool={pool} onBack={onBack} />}
        main={
          <VStack gap={DEFAULT_CARD_SPACER}>
            <PoolDetailsChart pool={pool} priceHistory={priceHistory} />
            <PoolDetailsTransactions pool={pool} transactions={transactions} />
          </VStack>
        }
        sidebar={<PoolDetailsSidebar pool={pool} />}
        footer={isMobile ? <MobileFooter pool={pool} /> : undefined}
      />
    </VStack>
  );
});

const LayoutWrapper = memo(function LayoutWrapper({ poolId, onBack }: Props) {
  return <PoolDetailsLayout poolId={poolId} onBack={onBack} />;
});

export default LayoutWrapper;
